<template>
  <component :is="iconComponent" :class="iconClass" />
</template>

<script setup lang="ts">
import { computed, h } from 'vue'
import {
  // Navigation
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  // Content
  BookOpenIcon,
  AcademicCapIcon,
  CalculatorIcon,
  // Actions
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  // Status
  LockClosedIcon,
  LockOpenIcon,
  CheckCircleIcon,
  // Audio/Media
  SpeakerWaveIcon,
  // Interactive
  BoltIcon,
  // Empty states
  BookmarkIcon,
  // Children
  UserGroupIcon,
  // Store
  ShoppingCartIcon,
  CurrencyDollarIcon,
  // Library
  BookmarkSquareIcon,
  // Backpack
  BriefcaseIcon,
  // Review
  ClipboardDocumentCheckIcon,
  // General
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/vue/24/outline'
import {
  CheckIcon as CheckIconSolid,
  XMarkIcon as XMarkIconSolid,
} from '@heroicons/vue/24/solid'

const props = defineProps<{
  name: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  solid?: boolean
}>()

const iconMap: Record<string, any> = {
  // Navigation
  'arrow-left': ArrowLeftIcon,
  'arrow-right': ArrowRightIcon,
  'chevron-left': ChevronLeftIcon,
  'chevron-right': ChevronRightIcon,
  'back': ArrowLeftIcon,
  'next': ArrowRightIcon,
  
  // Content Types
  'book': BookOpenIcon,
  'story': BookOpenIcon,
  'stem': AcademicCapIcon,
  'math': CalculatorIcon,
  'content': BookOpenIcon,
  
  // Actions
  'edit': PencilIcon,
  'pencil': PencilIcon,
  'delete': TrashIcon,
  'trash': TrashIcon,
  'add': PlusIcon,
  'plus': PlusIcon,
  'check': CheckIcon,
  'check-solid': CheckIconSolid,
  'close': XMarkIcon,
  'x': XMarkIcon,
  'x-solid': XMarkIconSolid,
  
  // Status
  'lock': LockClosedIcon,
  'locked': LockClosedIcon,
  'unlock': LockOpenIcon,
  'unlocked': LockOpenIcon,
  'completed': CheckCircleIcon,
  'check-circle': CheckCircleIcon,
  
  // Audio/Media
  'audio': SpeakerWaveIcon,
  'speaker': SpeakerWaveIcon,
  
  // Interactive
  'interactive': BoltIcon,
  'bolt': BoltIcon,
  
  // Empty states
  'empty': BookmarkIcon,
  'library': BookmarkIcon,
  
  // Children
  'child': UserGroupIcon,
  'children': UserGroupIcon,
  'user-group': UserGroupIcon,
  
  // Store
  'cart': ShoppingCartIcon,
  'shopping-cart': ShoppingCartIcon,
  'price': CurrencyDollarIcon,
  'dollar': CurrencyDollarIcon,
  
  // Library
  'bookmark': BookmarkSquareIcon,
  
  // Backpack
  'backpack': BriefcaseIcon,
  'briefcase': BriefcaseIcon,
  
  // Review
  'review': ClipboardDocumentCheckIcon,
  'clipboard': ClipboardDocumentCheckIcon,
  
  // General
  'search': MagnifyingGlassIcon,
  'filter': FunnelIcon,
  'funnel': FunnelIcon,
}

const iconComponent = computed(() => {
  const icon = iconMap[props.name]
  if (!icon) {
    console.warn(`Icon "${props.name}" not found. Available icons:`, Object.keys(iconMap))
    return null
  }
  return icon
})

const iconClass = computed(() => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8',
  }
  return sizeClasses[props.size || 'md']
})
</script>

